import type { FC } from 'react';
import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {
  Box,
  ListItemAvatar,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import {
  Iconify,
  MenuPopover,
  NameAvatar,
  TruncatedTooltipText,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

export interface NursingHomeOption {
  id: string;
  name: string;
}

export interface SelectNursingHomeViewProps {
  nursingHomes: NursingHomeOption[];
  selectedNursingHome: NursingHomeOption;
  onSelectNursingHome: (nursingHome: NursingHomeOption) => void;
}

const ITEM_HEIGHT = 60;

export const SelectNursingHomeView: FC<SelectNursingHomeViewProps> = ({
  nursingHomes,
  selectedNursingHome,
  onSelectNursingHome,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const menuItemRefs = useRef<Array<HTMLLIElement | null>>([]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
    setSearchQuery('');
    setFocusedIndex(-1);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(null);
    setFocusedIndex(-1);
  }, []);

  const handleSelectedNursingHome = useCallback(
    (nursingHome: NursingHomeOption) => {
      if (!open) {
        return;
      }

      onSelectNursingHome(nursingHome);
      handleClose();
    },
    [onSelectNursingHome, handleClose, open]
  );

  const filteredNursingHomes = useMemo(() => {
    if (!searchQuery.trim()) {
      return nursingHomes;
    }

    const query = searchQuery.toLowerCase();
    return nursingHomes.filter((nh) => nh.name.toLowerCase().includes(query));
  }, [nursingHomes, searchQuery]);

  const SCROLLBAR_HEIGHT = useMemo(() => {
    const maxVisibleItems = 5;
    const defaultHeight = maxVisibleItems * ITEM_HEIGHT;
    const nbrOfItems = filteredNursingHomes.length;
    if (nbrOfItems === 0) {
      return ITEM_HEIGHT;
    }

    return nbrOfItems > maxVisibleItems
      ? defaultHeight
      : nbrOfItems * ITEM_HEIGHT;
  }, [filteredNursingHomes]);

  // Reset focused index when filtered list changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredNursingHomes]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && menuItemRefs.current[focusedIndex]) {
      menuItemRefs.current[focusedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [focusedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (filteredNursingHomes.length === 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev + 1;
            return nextIndex >= filteredNursingHomes.length ? 0 : nextIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev - 1;
            return nextIndex < 0 ? filteredNursingHomes.length - 1 : nextIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredNursingHomes.length) {
            handleSelectedNursingHome(filteredNursingHomes[focusedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          handleClose();
          break;
      }
    },
    [filteredNursingHomes, focusedIndex, handleSelectedNursingHome, handleClose]
  );

  return (
    <>
      <SelectionButton
        direction="row"
        onClick={handleOpen}
        spacing={2}
        alignItems="center"
      >
        <Iconify
          icon={'mdi:home'}
          width={20}
          height={20}
          sx={{ minWidth: 20 }}
        />
        <TruncatedTooltipText text={`${selectedNursingHome?.name}`} noTooltip />
      </SelectionButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        arrow="top-left"
        sx={{
          mt: 0.5,
          ml: 6,
          width: 350,
          boxShadow: 'rgba(0, 0, 0, 0.15) 0px 5px 15px',
          pb: 2,
          paddingX: 1.2,
          '& .MuiMenuItem-root': {
            px: 1.5,
            height: ITEM_HEIGHT,
            borderRadius: 1.5,
          },
        }}
      >
        <Typography variant="h6" sx={{ p: 1.5, pb: 1 }}>
          {t('nav.nursing-home-selection')}
        </Typography>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('nav.search-nursing-homes')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: 'text.disabled', width: 20, height: 20, mr: 1 }}
                />
              ),
            }}
          />
        </Box>
        <Box style={{ overflowY: 'auto' }} sx={{ height: SCROLLBAR_HEIGHT }}>
          {filteredNursingHomes.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                p: 2,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              {t('table.no-results')}
            </Typography>
          ) : (
            filteredNursingHomes.map((nursingHome, index) => (
              <MenuItem
                key={nursingHome.id}
                ref={(el) => (menuItemRefs.current[index] = el)}
                onClick={() => handleSelectedNursingHome(nursingHome)}
                onMouseEnter={() => setFocusedIndex(index)}
                sx={{
                  bgcolor:
                    selectedNursingHome?.id === nursingHome.id
                      ? 'grey.200'
                      : focusedIndex === index
                      ? 'action.hover'
                      : '',
                }}
              >
                <ListItemAvatar sx={{ position: 'relative' }}>
                  <NameAvatar name={nursingHome.name} />
                </ListItemAvatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <TruncatedTooltipText
                    text={nursingHome.name}
                    maxWidth={250}
                    sx={{
                      typography: 'body2',
                      color: 'primary.main',
                    }}
                  />
                  {selectedNursingHome?.id === nursingHome.id && (
                    <TruncatedTooltipText
                      text={t('common.current-selection')}
                      maxWidth={250}
                      sx={{
                        typography: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
      </MenuPopover>
    </>
  );
};

const SelectionButton = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    mb: 5,
    cursor: 'pointer',
    bgcolor: 'secondary.light',
    pt: 1.6,
    pb: 1.4,
    pl: 2,
    pr: 2,
    borderRadius: 1.5,
  })
);
